import React, {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Job,
  LegendConfig,
  LegendType,
  ParsedReportParams,
  ReportTemplate,
} from '@ais-ui/websockets';
import ImageLayer from 'ol/layer/Image';
import Static from 'ol/source/ImageStatic';

interface ReportContextProps {
  children: ReactNode;
}

interface ReportContextValue {
  legend: LegendConfig<LegendType> | null;
  setLegend: Dispatch<SetStateAction<LegendConfig<LegendType> | null>>;
  reportOverlayRef: React.MutableRefObject<ImageLayer<any>>;
  isWaiting: boolean;
  setIsWaiting: Dispatch<SetStateAction<boolean>>;
  hasLoaded: boolean;
  setHasLoaded: Dispatch<SetStateAction<boolean>>;
  filterByMapView: boolean;
  setFilterByMapView: Dispatch<SetStateAction<boolean>>;
  showTracks: boolean;
  setShowTracks: Dispatch<SetStateAction<boolean>>;
  selectedJob: Job | null;
  setSelectedJob: Dispatch<SetStateAction<Job | null>>;
  selectedReportTemplate: ReportTemplate | null;
  setSelectedReportTemplate: Dispatch<SetStateAction<ReportTemplate | null>>;
  reportType: string | null;
  reportDetails: ParsedReportParams | null;
  setReportDetails: Dispatch<SetStateAction<ParsedReportParams | null>>;
  /** @deprecated Use hideReport() from useReportActions instead */
  resetReportContext: () => void;
  shouldRefetch: boolean;
  setShouldRefetch: Dispatch<SetStateAction<boolean>>;
  // New: feature-owned visibility state
  reportTableVisible: boolean;
  setReportTableVisible: Dispatch<SetStateAction<boolean>>;
  // Derived: is a report currently active (selected + visible)
  isReportActive: boolean;
}

const ReportContext = createContext<ReportContextValue | undefined>(undefined);

export const ReportProvider = ({ children }: ReportContextProps) => {
  const [legend, setLegend] = useState<LegendConfig<LegendType> | null>(null);
  const reportOverlayRef = useRef(
    new ImageLayer({
      source: new Static({ url: '', imageExtent: [] }),
    })
  );
  const [isWaiting, setIsWaiting] = React.useState<boolean>(false);
  const [hasLoaded, setHasLoaded] = React.useState<boolean>(false);
  const [filterByMapView, setFilterByMapView] = useState<boolean>(false);
  const [showTracks, setShowTracks] = useState<boolean>(false);

  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [selectedReportTemplate, setSelectedReportTemplate] =
    useState<ReportTemplate | null>(null);

  const [shouldRefetch, setShouldRefetch] = useState(false);
  
  // New: report table visibility (feature-owned)
  const [reportTableVisible, setReportTableVisible] = useState<boolean>(false);

  const reportType = useMemo(() => {
    if (!selectedReportTemplate) {
      return null;
    }
    return selectedReportTemplate.type;
  }, [selectedReportTemplate]);

  const [reportDetails, setReportDetails] = useState<ParsedReportParams | null>(
    null
  );

  // Derived state: report is active when both selected and visible
  const isReportActive = useMemo(() => {
    return selectedJob !== null && reportTableVisible;
  }, [selectedJob, reportTableVisible]);

  const resetReportContext: () => void = useCallback(() => {
    setLegend(null);
    setIsWaiting(false);
    setHasLoaded(false);
    setFilterByMapView(false);
    setShowTracks(false);
    setSelectedJob(null);
    setSelectedReportTemplate(null);
    setReportDetails(null);
    setReportTableVisible(false);
  }, [
    setLegend,
    setIsWaiting,
    setHasLoaded,
    setFilterByMapView,
    setShowTracks,
    setSelectedJob,
    setSelectedReportTemplate,
    setReportDetails,
    setReportTableVisible,
  ]);

  return (
    <ReportContext.Provider
      value={{
        legend,
        setLegend,
        reportOverlayRef,
        isWaiting,
        setIsWaiting,
        hasLoaded,
        setHasLoaded,
        filterByMapView,
        setFilterByMapView,
        showTracks,
        setShowTracks,
        selectedJob,
        setSelectedJob,
        selectedReportTemplate,
        setSelectedReportTemplate,
        reportType,
        reportDetails,
        setReportDetails,
        resetReportContext,
        shouldRefetch,
        setShouldRefetch,
        reportTableVisible,
        setReportTableVisible,
        isReportActive,
      }}
    >
      {children}
    </ReportContext.Provider>
  );
};

export const useReportContext = () => {
  const context = useContext(ReportContext);
  if (!context) {
    throw new Error('useReportContext must be used within a ReportProvider');
  }
  return context;
};
